import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { DirectSalesLine } from 'components/common/types/DirectSalesCampaign.types';
import { Line } from 'components/common/types/Deal.types';
import { getAdvertiser, getBrand, getProductCategory } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getDirectSalesMediaType } from 'store/dealManagement/selectors/getDirectSalesMediaType';
import { getDirectSalesNetworkCriteria } from 'store/dealManagement/selectors/getDirectSalesTargetsCriteria';

export interface MandatoryFieldsCheckResult {
  isAllocationTabEnabled: boolean;
  areDealInfoMandatoryFieldsFilled: boolean;
  isDataFiltersTabEnabled: boolean;
  areAllocationMandatoryFieldsFilled: boolean;
  areAllMandatoryFieldsFilled: boolean;
}

export const useCheckDirectSalesMandatoryFields = (activeLineId?: string): MandatoryFieldsCheckResult => {
  const mediaType = useSelector(getDirectSalesMediaType);
  const advertiser = useSelector(getAdvertiser);
  const brand = useSelector(getBrand);
  const productCategory = useSelector(getProductCategory);

  const currentLine = useSelector(getCurrentLine);
  const backupLines = useSelector(getBackupLines, shallowEqual);
  const currentLineNetworks = useSelector(getDirectSalesNetworkCriteria);

  const { targetLine, targetNetworks } = useMemo(() => {
    if (!activeLineId) {
      return {
        targetLine: currentLine,
        targetNetworks: currentLineNetworks,
      };
    }

    if (activeLineId === currentLine.lineId) {
      return {
        targetLine: currentLine,
        targetNetworks: currentLineNetworks,
      };
    }

    const foundLine = backupLines.find(({ lineId }: Line | DirectSalesLine) => lineId === activeLineId);

    if (!foundLine) {
      return {
        targetLine: currentLine,
        targetNetworks: currentLineNetworks,
      };
    }

    return {
      targetLine: foundLine,
      targetNetworks: (foundLine as DirectSalesLine).networkCriteria || [],
    };
  }, [activeLineId, backupLines, currentLine, currentLineNetworks]);

  const { startDate, endDate, environments, impressions, budget, frames, name } = targetLine;

  const isDataFiltersTabEnabled = false;

  const areDealInfoMandatoryFieldsFilled = useMemo(() => {
    return Object.values({
      startDate,
      endDate,
      advertiser,
      brand,
      productCategory,
      haveEnvironments: environments.length > 0,
      mediaType,
      name,
    }).every(Boolean);
  }, [startDate, endDate, advertiser, brand, productCategory, environments, mediaType, name]);

  const areAllocationMandatoryFieldsFilled = useMemo(() => {
    return Boolean(impressions || budget || frames || targetNetworks.length);
  }, [impressions, budget, frames, targetNetworks.length]);

  const areAllMandatoryFieldsFilled = useMemo(() => {
    return Object.values({
      areDealInfoMandatoryFieldsFilled,
      areAllocationMandatoryFieldsFilled,
    }).every(Boolean);
  }, [areDealInfoMandatoryFieldsFilled, areAllocationMandatoryFieldsFilled]);

  return {
    areDealInfoMandatoryFieldsFilled,
    isAllocationTabEnabled: areDealInfoMandatoryFieldsFilled,
    isDataFiltersTabEnabled,
    areAllocationMandatoryFieldsFilled,
    areAllMandatoryFieldsFilled,
  };
};
